import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { setPeriod } from '../state/actions';
import { useInceptionDate } from './useInceptionDate';
import { useRangePresets } from './useRangePresets';
import { usePeriod } from './usePeriod';
import { PerformancePeriodDTO } from '../types';
import { defaultPeriod } from '../AccountPerformanceReporting.constants';
import { useRouteParams } from './useRouteParams';

/**
 * When switching groups/accounts the selected period is sometimes not applicable
 * and must be changed programmatically
 */
export const useValidatePeriod = () => {
  const { accountId, groupId } = useRouteParams();

  const period = usePeriod();

  const inceptionDate = useInceptionDate();

  const rangePresets = useRangePresets();

  const dispatch = useDispatch();

  const startDate = period.dates[0];

  const sinceInceptionPeriod = useMemo(
    () => rangePresets.find(({ value }) => value === PerformancePeriodDTO.AllTime),
    [rangePresets],
  );

  useEffect(() => {
    // Current period is Since Inception ➜ we need to update the dates because the new
    // group/account may have a different inception date
    // If the new group/account does not have an inception date ➜ select the default period since
    // without an inception date we cannot display a start date in the calendar (note: without an
    // inception date, the default period is always valid)
    if (period.value === PerformancePeriodDTO.AllTime) {
      dispatch(setPeriod(inceptionDate ? sinceInceptionPeriod : defaultPeriod));

      return;
    }

    // All periods are valid when we don't have an inception date
    if (!inceptionDate) {
      return;
    }

    // Current selected period is applicable
    if (moment(inceptionDate).isSameOrBefore(startDate)) {
      return;
    }

    // When the period is not applicable we should select the default period (as long as
    // it is valid)
    if (moment(inceptionDate).isSameOrBefore(defaultPeriod.dates[0])) {
      dispatch(setPeriod(defaultPeriod));

      return;
    }

    // If the default period is not applicable we select the since inception period instead.
    if (sinceInceptionPeriod) {
      dispatch(setPeriod(sinceInceptionPeriod));
    }
  }, [
    groupId,
    accountId,
    inceptionDate,
    rangePresets,
    sinceInceptionPeriod,
    dispatch,
    period.dates,
    period.value,
    startDate,
  ]);
};
